import { useEffect, useRef, useState } from "react";
import {
  CommunicationService,
  MessageEntityTypesEnum
} from "@propertelligent/client-api";
import AddTraspButton from "../../firstTimeUserExperience/commonCom/AddTraspButton";

import { UserLoginData } from "../../common/charts/Enums";
import { formatChatDate } from "../../../utils/DateHelper";
import { MessageChannelTypesEnum } from "../../../constants/messageChannelTypes";
import EmptyState from "../../../newComponents/emptystate";
import apiHelper from "../../../utils/apiHelper";
import { handleApiErrors } from "../../../utils/handleApiErrors";
import AddButtonWithArrow from "../../firstTimeUserExperience/commonCom/AddButtonWithArrow";
import { CustomInputTextArea } from "../../common/formControls";
import { Background } from "../../GlobalCss/Background";
import UtilHelper from "../../../utils/UtilHelper";
import NameAvatarComponent from "../../../newComponents/NameAvatar/NameAvatar";
import { Colors } from "../../GlobalCss/Colors";
import { TextCss } from "../../GlobalCss/TextCss";
import SimpleSpinner from "../../loader";

const ChatBox = ({
  entityId,
  isMobileView = false,
  showMessage = false,
  entityTypeId,
  setShowMessage,
  setShowSection = undefined,
  chatname,
  setChatName,
  fetchData,
  setEntityId,
  setEntityTypeId,
  setmessageData = undefined,
  messageData = undefined,
  tenantList = [],
  tenantId,
  messageChannel = [],
  firstName,
  lastName,
  inputRef = null,
  hasWritePermission = true,
  fetchList = () => { }
}) => {
  const [chatdata, setChatdata] = useState([]);
  const [chatLoading, setChatLoading] = useState(false);
  const [inputValue, setInputValue] = useState("");
  const [listdata, setList] = useState([]);
  const [selectedItem, setSelectedItem] = useState(null);
  const [isSending, setIsSending] = useState(false);

  const scrollDown = useRef(null);
  const chatboxBottomRef = useRef<HTMLDivElement>(null);

  // Auto-focus the message textarea whenever the selected conversation changes, so the user can
  // start typing immediately instead of having to click into the field first. CustomInputTextArea
  // doesn't forward a ref to its underlying <textarea>, so this queries the DOM within the active
  // chatboxbottom container instead of modifying that shared component. Skipped on mobile -- it
  // would otherwise pop the on-screen keyboard every time the user switches conversations.
  useEffect(() => {
    if (!entityId || isMobileView) return;
    chatboxBottomRef.current?.querySelector("textarea")?.focus();
  }, [entityId, isMobileView]);

  const name = chatname?.split(/\s+/).filter(Boolean) || [];

  // The "Search Tenant" list below is filtered entirely client-side (checkMatch(), against the
  // already-fetched tenantList prop) -- no network round-trip is needed on keystroke, so this
  // effect only resets the selection once the input is cleared. It used to also call
  // fetchMessages()/fetchList() on every keystroke here: fetchMessages() was always a no-op
  // (guarded on entityId, which is null while this input is shown) and fetchList() redundantly
  // re-fetched the parent's tenant list using the PARENT's own inputValue state (a same-named
  // but different value than this component's local one), not this input's typed text.
  useEffect(() => {
    if (inputValue === "") {
      setSelectedItem(null);
    }
  }, [inputValue]);

  // showLoading=false for the post-send refresh below -- that's a background re-sync of a
  // conversation the user is already looking at, not a switch to a different one, so it should
  // update chatdata in place rather than hiding the whole history behind a spinner.
  const fetchMessages = (showLoading = true) => {
    const fetchFn =
      entityTypeId === MessageEntityTypesEnum.Tenant
        ? CommunicationService.getApiMessageTenantlist
        : entityTypeId === MessageEntityTypesEnum.Contractor
          ? CommunicationService.getApiMessageContractorlist
          : null;

    if (!fetchFn || !entityId) return;

    if (showLoading) setChatLoading(true);
    apiHelper(fetchFn, { showNotification: false }, entityId)
      .then((res) => {
        setChatdata(res?.data?.success || []);
      })
      .catch(() => { })
      .finally(() => {
        if (showLoading) setChatLoading(false);
      });
  };

  useEffect(() => {
    if (setmessageData) {
      setmessageData({
        ...messageData,
        messageData: "",
        entityTypeId,
        entityId
      });
    }
    scrollDown?.current?.scrollIntoView();
    fetchMessages();
  }, [entityId, entityTypeId, chatname, messageData?.entityId]);


  const sendMessage = () => {
    if (
      !messageData?.messageData?.trim() ||
      isSending
    ) return;

    setIsSending(true);

    apiHelper(
      CommunicationService.postApiMessage,
      { showNotification: false },
      messageData
    )
      .then((res) => {
        if (res?.data?.success && setmessageData) {
          setmessageData({
            ...messageData,
            messageData: "",
            entityTypeId,
            entityId
          });
          fetchMessages(false);
          if (typeof fetchData === "function") {
            // Background refresh only (updates the tenant list's last-message preview/order in
            // place) -- not a user-driven switch, so it must not show the full-list spinner.
            fetchData(false);
          }
          return;
        }
        // Fail-closed PhoneNumber (and other field) errors from Message create — toast via PubSub.
        if (res?.errors) {
          handleApiErrors(res.errors, () => {});
        }
      })
      .finally(() => {
        setIsSending(false);
      });
  };

  const resetSearch = () => {
    setList(tenantList.map((item) => item.name));
  };

  const checkMatch = () => {
    if (!inputValue) return null;

    const filteredTenants = tenantList.filter(
      (item) =>
        item?.name?.toLowerCase().includes(inputValue.toLowerCase()) &&
        selectedItem?.name !== item.name
    );

    if (filteredTenants.length === 0) {
      return (
        <li className="lists showMe">
          No matching results. Please try again
        </li>
      );
    }

    return filteredTenants.map((item, index) => (
      <li
        key={index}
        className="lists showMe"
        onClick={() => complete(item)}
      >
        {item.name}
      </li>
    ));
  };

  const complete = (value) => {
    setEntityTypeId(MessageEntityTypesEnum.Tenant);
    setEntityId(value.id);
    setChatName(value.name);
    setInputValue("");
    setSelectedItem(value);
  };

  const handleGoBackClick = () => {
    setShowMessage(false);
  };

  const renderMessage = (message) => {
    const urlRegex = /(https?:\/\/[^\s]+)/g;
    return message.split(urlRegex).map((part, index) =>
      urlRegex.test(part) ? (
        <a
          key={index}
          href={part}
          target="_blank"
          rel="noopener noreferrer"
          style={{ color: "blue", textDecoration: "underline" }}
        >
          {part}
        </a>
      ) : (
        part
      )
    );
  };

  useEffect(() => {
    if (scrollDown?.current) {
      scrollDown.current.scrollIntoView();
    }
  }, [chatdata]);


  return (
    <>
      {!entityId ? (
        <div className="chatboxContainer">
          <div className="chatboxtop">
            <div className="selectTanent">
              <div className="box-container">
                <input
                  type="text"
                  id="userName"
                  placeholder="Search Tenant"
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  onFocus={resetSearch}
                  ref={inputRef}
                />
              </div>
            </div>
            {!selectedItem?.name && inputValue !== "" && (
              <ul className="selectedChat">{checkMatch()}</ul>
            )}
          </div>

          {hasWritePermission && (
            <div className="chatboxbottom">
              <CustomInputTextArea
                rows={1}
                cols={80}
                value={messageData?.messageData || ""}
                required
                placeholder="Type your message here...."
                name="messageData"
                myClassName="serchInput"
                width="100%"
                onChange={(e) =>
                  setmessageData({
                    ...messageData,
                    messageData: e.target.value
                  })
                }
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    sendMessage();
                  }
                }}
              />
              <AddTraspButton
                bname="Send"
                onClick={sendMessage}
                border={false}
              />
            </div>
          )}
        </div>
      ) : (
        <>
          <div className="chatboxContainer">
            {tenantId && entityId && (
              <div className="chatItemContaner">
                {messageChannel?.length === 0 ? (
                  <EmptyState
                    heading="No Message Here"
                    bodytext="Please click the button below to start the conversation"
                    actionNode={
                      <AddButtonWithArrow
                        buttonname="Start Conversation"
                        onClick={undefined}
                      />
                    }
                    isBorder={false}
                  />
                ) : (
                  messageChannel?.map((item, i) => {
                    const [first, last] = item?.name?.split(" ") || [];
                    return (
                      <div
                        key={i}
                        className="chatItem"
                        style={{
                          background:
                            item.entityId === entityId
                              ? "var(--Primary-Next-level-Lighter, #EBF5FF)"
                              : ""
                        }}
                        onClick={() => {
                          setEntityId(item.entityId);
                          setEntityTypeId(item?.entityTypeId);
                          setChatName(item?.name);
                          setShowSection?.("chat");
                          fetchData(true);
                        }}
                      >
                        <div className={Background.LightBlue.Square.square40}> <div className="blue">{UtilHelper.getInitials(name[0] + " " + name[1])}</div> </div>

                        {/* <div className="userIcon">
                          <NameAvatarComponent
                            firstName={first}
                            lastName={last}
                            heightWidth={40}
                          /> 
                        </div>*/}

                        <div className="chatDetailsView">
                          <div className="d-flex flex-row justify-content-between align-items-center w-100">
                            <p className="normal_text black">{item?.name}</p>
                            <p className="sub_text grey_subtext1">
                              {formatChatDate(item?.lastMessageDateTime)}
                            </p>
                          </div>
                          <div className="message_count sub_text d-flex justify-content-between align-items-center w-100">
                            <p className="sub_text grey_subtext1" id="message-data">
                              {item?.lastMessageData}
                            </p>
                            {item.unreadMessagesCount > 0 && (
                              <div className="meesageCount">
                                <p className="small_text white">
                                  {item.unreadMessagesCount}
                                </p>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            )}

            <div className="chatDetails">
              {/* <div className={Background.LightBlue.Square.square40}> <div className="blue">{UtilHelper.getInitials(tenantId ? firstName.split(" ")[0] : name[0]  + " " + tenantId ? lastName.split(" ")[0] : name[1] )}</div> </div> */}

              {/* <NameAvatarComponent
                firstName={tenantId ? firstName.split(" ")[0] : name[0] || ""}
                lastName={tenantId ? lastName.split(" ")[0] : name[1] || ""}
                heightWidth={40}
              /> */}
              <NameAvatarComponent
                firstName={tenantId ? firstName.split(" ")[0] : name[0] || ""}
                lastName={tenantId ? lastName.split(" ")[0] : name[1] || ""}
                heightWidth={40}
                shape="square"
                className={TextCss.heading_700_16}
                color={Colors.blue.regular}
                backgroundColor={Colors.blue.lighter}
              />
              <p className="sub_heading black text-start">
                {tenantId ? `${firstName}${lastName}` : chatname}
              </p>
            </div>

            <div className="messageSection">
              {chatLoading ? (
                <div className="d-flex justify-content-center align-items-center w-100 h-100">
                  <SimpleSpinner myClassName="Icon40" />
                </div>
              ) : chatdata.map((item) => {
                const isCurrentUser =
                  item.createdById ===
                  parseInt(sessionStorage.getItem(UserLoginData.user_id));
                return (
                  <div
                    key={item.id}
                    className={isCurrentUser ? "outgoingMessage" : "incomingMessage"}
                  >
                    <div className="chatMessage">
                      <p className="normal_text black receiver">
                        {(item as any)?.channelTypeId === MessageChannelTypesEnum.WhatsApp && (
                          <span className="whatsapp-channel-badge">WhatsApp</span>
                        )}
                        {renderMessage(item.messageData)}
                      </p>
                      <p className="sub_text grey_subtext1 received-date">
                        {new Date(item?.created).toLocaleString()}
                      </p>
                    </div>

                  </div>
                );
              })}
              <div className="scrolldown" ref={scrollDown} />
            </div>
            {hasWritePermission && (
              <div className="chatboxbottom" ref={chatboxBottomRef}>
                <CustomInputTextArea
                  rows={1}
                  cols={80}
                  value={messageData?.messageData || ""}
                  required
                  placeholder="Type your message here...."
                  name="messageData"
                  myClassName="serchInput"
                  width="100%"
                  onChange={(e) =>
                    setmessageData({
                      ...messageData,
                      messageData: e.target.value
                    })
                  }
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      sendMessage();
                    }
                  }}
                />
                <AddTraspButton
                  bname="Send"
                  onClick={sendMessage}
                  border={false}
                />
              </div>
            )}
          </div>
        </>
      )}
    </>
  );
};

export default ChatBox;


